import { put, call, select, takeLatest } from 'redux-saga/effects';
import { userInfoSelector } from '../../selectors/auth';

import {
  clearStatus,
  hideError,
  requestStatus,
  showErrorMessage,
} from '../../actions/global';
import { CUSTOMER } from '../../../components/global/Server/endpoints';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import { HTTP_SUCCESS } from '../../constants/global';
import {
  storeAllCustomerPending,
  storeAllCustomerPoints,
  storeAllCustomers,
  storeAllHistoricalCustomers,
  storeAllRaffleCustomers,
  storeCustomerTicket,
  storePointHistory,
  storeUpdatedCustomer,
  storeUpdatedCustomerImageWithPreview,
  storeUpdatedCustomerWithPreview,
} from '../../actions/customer';
import {
  DELETE_CUSTOMER_ACCOUNT,
  DELETE_CUSTOMER_PENDING,
  ENROLL_CUSTOMER,
  GET_CUSTOMER_POINT_HISTORY,
  LIST_ALL_CUSTOMERS,
  LIST_ALL_HISTORICAL_CUSTOMERS,
  LIST_CUSTOMERS_PENDING,
  LIST_CUSTOMERS_POINTS,
  LIST_RAFFLE_CUSTOMERS,
  PREVIEW_CUSTOMER_FILE,
  PREVIEW_CUSTOMER_IMAGE,
  PREVIEW_POINT_WATCHING_IMAGE,
  RESTORE_HISTORICAL_CUSTOMER,
  SAVE_CUSTOMER,
  SET_CUSTOMER_TICKET,
  VALIDATE_CUSTOMER_NAME,
} from '../../constants/customer';
import { setRefresh } from '../../actions/dashboard';
import AuthUtil from '../../AuthUtil';

function* listAllCustomersSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.LIST_ALL_CUSTOMER, action.data)
    );
    if (response.data.success) {
      yield put(storeAllCustomers(response.data));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listAllHistoricalCustomersSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.LIST_ALL_HISTORICAL_CUSTOMER, {
        locationId: action.locationId,
      })
    );
    if (response.data.success) {
      yield put(storeAllHistoricalCustomers(response.data));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* previewImageSaga(action) {
  yield call(clearStatus);
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.PREVIEW_IMAGE, action.previewRequest)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };

    if (!response.data.success) {
      yield put(showErrorMessage(response.data.message));
    } else {
      yield put(
        storeUpdatedCustomerImageWithPreview(
          response.data.customerItem.previewedFile
        )
      );
    }
    yield put(requestStatus(status));
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listCustomerPendingSaga(action) {
  yield put(hideError());
  yield put(setRefresh(true));
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.LIST_CUSTOMER_PENDING, {
        locationId: action.locationId,
        date: action.date,
      })
    );
    if (response.data.success) {
      yield put(storeAllCustomerPending(response.data));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
  yield put(setRefresh(false));
}

function* deleteCustomerPendingSaga(action) {
  yield put(hideError());
  try {
    // actingUserId: the navbar-switched employee, so the audit trail names
    // who deleted the match (the token belongs to the device login).
    const user = yield select(userInfoSelector);
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.DELETE_CUSTOMER_PENDING, {
        intent: action.intent,
        pendingId: action.pendingId,
        locationId: action.locationId,
        date: action.date,
        actingUserId: user?.userId,
      })
    );
    if (response.data.success) {
      if (action.intent === 'pending') {
        yield put(storeAllCustomerPending(response.data));
      } else {
        yield put(storeAllCustomerPoints(response.data));
      }
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* setCustomerTicketSaga(action) {
  yield put(hideError());
  yield put(setRefresh(true));
  try {
    const fields = {
      userId: action.userId,
      pendingId: action.pendingId,
      ticket: action.ticket,
    };
    let response;
    if (action.photo) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(fields));
      formData.append('photo', action.photo, action.photo.name);
      response = yield call(() =>
        AuthUtil.postMultipartRequest(CUSTOMER.SET_CUSTOMER_TICKET, formData)
      );
    } else {
      response = yield call(() =>
        AuthUtil.postRequest(CUSTOMER.SET_CUSTOMER_TICKET, fields)
      );
    }
    if (response.data.success) {
      yield put(storeCustomerTicket(response.data.customerPendingItem));
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, 'refresh');
      }
    } else {
      if (action.callback && typeof action.callback === 'function') {
        action.callback(
          response.data.message || 'error',
          null,
          response.data.code
        );
      }
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    if (action.callback && typeof action.callback === 'function') {
      action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
  yield put(setRefresh(false));
}

function* enrollCustomerSaga(action) {
  yield put(hideError());
  try {
    const photoFile = action.photo?.file || null;

    const formData = new FormData();
    formData.append(
      'data',
      JSON.stringify({
        phone: action.phone,
        locationId: action.locationId,
        points: action.points,
        manual: action.manual,
      })
    );
    if (photoFile) {
      formData.append('photo', photoFile, photoFile.name);
    }

    const response = yield call(() =>
      AuthUtil.postMultipartRequest(CUSTOMER.ENROLL_CUSTOMER, formData)
    );
    if (response.data.success) {
      // yield put(storeCustomerTicket(response.data.customerPendingItem))
      if (action.callback && typeof action.callback === 'function') {
        action.callback(null, response.data.message); // Pass response or error as needed
      }
    } else {
      action.callback(response.data.message, null);
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* listCustomerPointsSaga(action) {
  yield put(hideError());
  yield put(setRefresh(true));
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.LIST_CUSTOMER_POINTS, {
        userId: action.userId,
        locationId: action.locationId,
        date: action.date,
      })
    );
    if (response.data.success) {
      yield put(storeAllCustomerPoints(response.data));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
  yield put(setRefresh(false));
}

function* saveCustomerSaga(action) {
  yield put(hideError());

  try {
    const { newFiles, ...rest } = action.data || {};
    const photo = newFiles?.[0]?.file || null;

    const formData = new FormData();
    formData.append('data', JSON.stringify(rest));
    if (photo) {
      formData.append('photo', photo, photo.name);
    }

    const response = yield call(() =>
      AuthUtil.postMultipartRequest(CUSTOMER.SAVE_CUSTOMER, formData)
    );

    if (!response.data.success) {
      yield put(showErrorMessage(response.data.message));
      if (action.onError) action.onError(response.data.message);
      return;
    }

    const savedCustomer = response.data.customer;

    const mergedCustomer = {
      ...savedCustomer,
      capturedImageURL: action.data.capturedImageURL || null,
    };

    yield put(storeUpdatedCustomer(mergedCustomer));

    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
    if (action.onError) action.onError(SERVER_FAILED_ERROR_MESSAGE);
  }
}

function* previewCustomerFileSaga(action) {
  yield call(clearStatus);
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.PREVIEW_FILE, action.previewRequest)
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };

    if (!response.data.success) {
      yield put(showErrorMessage(response.data.message));
    } else {
      yield put(storeUpdatedCustomerWithPreview(response.data.customerItem));
    }
    yield put(requestStatus(status));
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* deleteCustomerSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.DELETE_CUSTOMER, action.data)
    );
    if (response.data.success) {
      yield put(storeAllCustomers(response.data));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

// Customer search on the bonus and raffle screens is debounced per keystroke,
// so this runs far more often than it used to. Every path stores a result -
// including the failures - because the screens clear their in-flight spinner
// when the customer list changes, and a search that never resolves would leave
// it turning.
function* listAllRaffleCustomersSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.RAFFLE_CUSTOMERS, action.data)
    );
    if (response.data.success) {
      yield put(storeAllRaffleCustomers(response.data.customers));
    } else {
      yield put(storeAllRaffleCustomers([]));
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(storeAllRaffleCustomers([]));
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* previewPointWatchingImageSaga(action) {
  yield call(clearStatus);
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(
        CUSTOMER.PREVIEW_POINT_WATCHING_IMAGE,
        action.previewRequest
      )
    );
    const status = {
      status: response.status,
      statusText: response.statusText,
      success: response.status === HTTP_SUCCESS,
    };

    if (!response.data.success) {
      yield put(showErrorMessage(response.data.message));
    } else {
      action.callback(response.data);
    }
    yield put(requestStatus(status));
  } catch (e) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

//point history
function* getPointHistorySaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.GET_POINT_HISTORY, action.data)
    );
    if (response.data.success) {
      yield put(storePointHistory(response.data.pointHistory));
    } else {
      yield put(showErrorMessage(response.data.message));
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* checkCustomerNameSaga(action) {
  yield put(hideError());
  try {
    const response = yield call(() =>
      AuthUtil.postRequest(CUSTOMER.VALIDATE_CUSTOMER_NAME, action.data)
    );
    if (action.callback && typeof action.callback === 'function') {
      action.callback(
        response.data.success,
        response.data.message,
        response.data.customers
      );
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
  }
}

function* restoreHistoricalCustomerSaga(action) {
  yield put(hideError());

  try {
    const response = yield call(() =>
      AuthUtil.postRequest(
        CUSTOMER.RESTORE_HISTORICAL_CUSTOMER,
        action.customerId
      )
    );

    if (!response.data.success) {
      yield put(showErrorMessage(response.data.message));
      if (action.onError) action.onError(response.data.message);
      return;
    }

    if (action.onSuccess) {
      action.onSuccess(response.data.message);
    }
  } catch (error) {
    yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
    if (action.onError) action.onError(SERVER_FAILED_ERROR_MESSAGE);
  }
}

export default function* customerSagas() {
  yield takeLatest(LIST_ALL_CUSTOMERS, listAllCustomersSaga);
  yield takeLatest(SAVE_CUSTOMER, saveCustomerSaga);
  yield takeLatest(DELETE_CUSTOMER_ACCOUNT, deleteCustomerSaga);
  yield takeLatest(PREVIEW_CUSTOMER_FILE, previewCustomerFileSaga);
  yield takeLatest(LIST_CUSTOMERS_POINTS, listCustomerPointsSaga);
  yield takeLatest(LIST_CUSTOMERS_PENDING, listCustomerPendingSaga);
  yield takeLatest(DELETE_CUSTOMER_PENDING, deleteCustomerPendingSaga);
  yield takeLatest(SET_CUSTOMER_TICKET, setCustomerTicketSaga);
  yield takeLatest(ENROLL_CUSTOMER, enrollCustomerSaga);
  yield takeLatest(PREVIEW_CUSTOMER_IMAGE, previewImageSaga);
  yield takeLatest(LIST_RAFFLE_CUSTOMERS, listAllRaffleCustomersSaga);
  yield takeLatest(PREVIEW_POINT_WATCHING_IMAGE, previewPointWatchingImageSaga);
  yield takeLatest(GET_CUSTOMER_POINT_HISTORY, getPointHistorySaga);
  yield takeLatest(VALIDATE_CUSTOMER_NAME, checkCustomerNameSaga);
  yield takeLatest(
    LIST_ALL_HISTORICAL_CUSTOMERS,
    listAllHistoricalCustomersSaga
  );
  yield takeLatest(RESTORE_HISTORICAL_CUSTOMER, restoreHistoricalCustomerSaga);
}
